import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from "react-fullscreen-loading";
import { toast } from "react-hot-toast";
import DataTable from "react-data-table-component";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import { FaEye, FaCopy } from "react-icons/fa";
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import { Tooltip } from "react-tooltip";
import { Can, useAuth } from "../../../authentication/AuthContext";
import TruncatedText, { TruncatedTextTooltip, DateCell, COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";

export default function ResellersList() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [TableData, setTableData] = useState([]);
  const [screenloader, setScreenloader] = useState(true);
  const [TotalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("rp.created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [pending, setPending] = useState(true);
  const [email, setEmail] = useState("");

  const getList = async () => {
    try {
      const filters = {};
      if (email) {
        filters["filters[0][column]"] = "u.email";
        filters["filters[0][operator]"] = "LIKE";
        filters["filters[0][value]"] = `%${email}%`;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/resellers/`,
        {
          params: { limit: pageSize, page, sort_by: sortBy, sort_order: sortOrder, ...filters },
          headers: { "access-token": localStorage.getItem("accessToken") },
        }
      );
      setScreenloader(false);
      setPending(false);
      if (response.data.status) {
        setTotalRecords(response.data.meta.pagination.total);
        setTableData(response.data.resellers);
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      setScreenloader(false);
      setPending(false);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    setPending(true);
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder]);

  const handleSearch = (e) => {
    if (e.key === "Enter") getList();
  };

  const columns = [
    {
      name: "Actions",
      // Hidden entirely rather than left as an empty column: omit drops the
      // header as well as the cells.
      omit: !(can('admin.resellers.show')),
      sortable: false,
      cell: (row) => (
        <>
          <Can permission="admin.resellers.show">
            <FaEye
              data-tooltip-id="reseller-view"
              data-tooltip-content="View detail"
              data-tooltip-place="top"
              className="edit-icon"
              onClick={() => navigate(`/Admin/Resellers/${row.id}`)}
            />
          </Can>
        </>
      ),
    },
    {
      name: "Profile ID",
      selector: (row) => row.id,
      sortable: false,
      cell: (row) => (
        <div
          style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
          onClick={() => { navigator.clipboard.writeText(String(row.id)); toast.success("Profile ID copied!"); }}
          data-tooltip-id="reseller-copy-id"
          data-tooltip-content="Click to copy Profile ID"
          data-tooltip-place="top"
        >
          <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>{row.id}</span>
          <FaCopy style={{ color: "#94a3b8", fontSize: "11px" }} />
        </div>
      ),
    },
    {
      name: "Reseller Code",
      grow: 2,
      selector: (row) => row.reseller_code,
      sortable: false,
      cell: (row) => (
        <span style={{
          fontFamily: "monospace",
          fontWeight: 700,
          letterSpacing: "2px",
          backgroundColor: "#e2e7f1",
          padding: "2px 10px",
          borderRadius: "6px",
          fontSize: "13px",
        }}>
          {row.reseller_code}
        </span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortField: "u.email",
      sortable: true,
      grow: 2,
      minWidth: `${COLUMN_WIDTHS.email}px`,
      cell: (row) => <TruncatedText text={row.email} maxWidth={COLUMN_WIDTHS.email} />,
    },
    {
      name: "Name",
      grow: 1.5,
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: (row) => `${row.fname} ${row.lname}`,
      cell: (row) => <TruncatedText text={`${row.fname || ""} ${row.lname || ""}`.trim()} maxWidth={COLUMN_WIDTHS.name} />,
    },
    {
      name: "Organization",
      selector: (row) => row.organization,
      sortField: "u.organization",
      sortable: true,
      grow: 1.5,
      minWidth: `${COLUMN_WIDTHS.organization}px`,
      cell: (row) => <TruncatedText text={row.organization} maxWidth={COLUMN_WIDTHS.organization} />,
    },
    {
      name: "Total Credits",
      selector: (row) => row.total_credits,
      sortField: "rp.total_credits",
      sortable: true,
      cell: (row) => <div style={{ fontWeight: 600 }}>{row.total_credits.toLocaleString()}</div>,
    },
    {
      name: "Available Credits",
      selector: (row) => row.available_credits,
      sortField: "rp.available_credits",
      sortable: true,
      cell: (row) => (
        <div style={{
          fontWeight: 600,
          color: row.available_credits > 0 ? "#016641" : "#dc3545",
        }}>
          {row.available_credits.toLocaleString()}
        </div>
      ),
    },
    {
      name: "Users",
      selector: (row) => row.user_count,
      sortable: false,
      cell: (row) => <div>{row.user_count}</div>,
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      sortField: "rp.created_at",
      sortable: true,
      grow: 1.5,
      minWidth: `${COLUMN_WIDTHS.date}px`,
      cell: (row) => <DateCell value={ShowTime(row.created_at)} />,
    },
  ];

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="row mb-3">
              <div className="col-md-6">
                <h2>Resellers</h2>
              </div>
            </div>

            <div className="filters row mb-3 g-3">
              <div className="col-md-4 col-12">
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Search by email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                  {email && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => getList()}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="table-wrapper">
              <DataTable
                columns={columns}
                data={TableData}
                dense
                highlightOnHover
                persistTableHead
                pointerOnHover
                responsive
                progressPending={pending}
                progressComponent={<CustomLoader />}
                pagination
                paginationServer
                paginationPerPage={pageSize}
                paginationTotalRows={TotalRecords}
                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                onChangePage={(p) => setPage(p)}
                onChangeRowsPerPage={(rows, p) => { setPageSize(rows); setPage(p); }}
                sortServer
                onSort={(col, dir) => { setSortBy(col.sortField); setSortOrder(dir); }}
              />
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="reseller-view" style={{ zIndex: 99 }} />
      <Tooltip id="reseller-copy-id" style={{ zIndex: 99 }} />
      <TruncatedTextTooltip />
    </div>
  );
}
